import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Heart, Globe, ChevronDown } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { LANGS } from "../i18n";
import { api } from "../lib/api";

export default function Navbar() {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [site, setSite] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/settings").then(r => setSite(r.data?.site || {})).catch(() => {});
    const handler = () => api.get("/settings").then(r => setSite(r.data?.site || {})).catch(() => {});
    window.addEventListener("lel-settings-updated", handler);
    return () => window.removeEventListener("lel-settings-updated", handler);
  }, []);

  const items = [
    { to: "/", label: t("nav.home"), tid: "nav-home" },
    { to: "/a-propos", label: t("nav.about"), tid: "nav-about" },
    { to: "/nos-actions", label: t("nav.actions"), tid: "nav-actions" },
    { to: "/nos-programmes", label: t("nav.programs"), tid: "nav-programs" },
    { to: "/nos-resultats", label: t("nav.results"), tid: "nav-results" },
    { to: "/actualites", label: t("nav.news"), tid: "nav-news" },
    { to: "/soutenir", label: t("nav.support"), tid: "nav-support" },
    { to: "/contact", label: t("nav.contact"), tid: "nav-contact" },
  ];

  const linkCls = ({ isActive }) =>
    `text-sm font-medium tracking-wide transition ${isActive ? "text-gold" : "text-ivory/90 hover:text-gold"}`;

  const current = LANGS.find(l => l.code === lang);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-deep-blue/85 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-brand">
          {site.logo_url ? (
            <img src={site.logo_url} alt="Logo" className="w-11 h-11 rounded-full object-cover border border-gold/40" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold to-warm-orange grid place-items-center shadow-lg">
              <span className="font-display text-deep-blue text-lg font-bold">L</span>
            </div>
          )}
          <div className="hidden sm:block">
            <div className="font-display text-ivory text-lg leading-none tracking-tight">{site.logo_text || "Larmes en Lumière"}</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-gold/80 mt-1">{site.logo_subtitle || "Humanitaire"}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {items.map(i => (
            <NavLink key={i.to} to={i.to} className={linkCls} data-testid={i.tid}>{i.label}</NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button onClick={() => setLangOpen(!langOpen)} className="hidden md:flex items-center gap-1.5 text-ivory/90 hover:text-gold text-sm" data-testid="lang-toggle">
              <Globe size={16} /> <span>{current?.flag} {lang.toUpperCase()}</span> <ChevronDown size={14} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-ivory rounded-lg shadow-xl border border-black/5 py-1">
                {LANGS.map(l => (
                  <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-pearl text-deep-blue"
                    data-testid={`lang-option-${l.code}`}>
                    {l.flag} {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => navigate("/faire-un-don")} className="btn-gold hidden md:flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="nav-donate-btn">
            <Heart size={16} className="fill-deep-blue" /> {t("nav.donate")}
          </button>
          <button className="lg:hidden text-ivory" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-deep-blue">
          <div className="px-5 py-4 flex flex-col gap-3">
            {items.map(i => (
              <NavLink key={i.to} to={i.to} onClick={() => setOpen(false)} className="text-white text-base font-medium py-3 border-b border-white/10 hover:text-gold transition" data-testid={`m-${i.tid}`}>{i.label}</NavLink>
            ))}
            <button onClick={() => { setOpen(false); navigate("/faire-un-don"); }} className="btn-gold px-5 py-3 rounded-full text-sm font-semibold" data-testid="m-nav-donate">
              ❤ {t("nav.donate")}
            </button>
            <div className="flex gap-2 pt-2">
              {LANGS.map(l => (
                <button key={l.code} onClick={() => setLang(l.code)} className={`px-3 py-1.5 rounded-full text-xs ${lang === l.code ? "bg-gold text-deep-blue" : "bg-white/10 text-ivory"}`} data-testid={`m-lang-${l.code}`}>{l.flag} {l.code.toUpperCase()}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
